// storage.ts — localStorage persistence for the scenario list. Versioned key so a future
// shape change can migrate or discard cleanly instead of crashing on old data.
// Bumped v1 -> v2 when the nav/settings rework added required ForecastFormState fields
// (kestPercent, distributionFrequency) and dropped compareMode (superseded by the
// dedicated #vergleich page) — old v1 data is simply discarded, never partially trusted.
// Bumped v2 -> v3 for the same reason when Sparplan-Dynamik added a required
// contributionStepUpPercent field to both ForecastFormState and BacktestFormState.
// Bumped v3 -> v4 when Entnahmeplan added required ForecastFormState fields
// (withdrawalEnabled, withdrawalMonthlyGrossEur, withdrawalYears) — UNLIKE the two
// bumps above, this one does NOT discard old data: loadPersistedState reads the legacy
// v3 key as a migration source and merges each scenario field-by-field over the current
// defaults, so any missing field (in particular withdrawalEnabled) backfills to its
// default ("off") instead of the whole scenario being thrown away. New writes always go
// to the v4 key, so this is a one-time, one-directional migration.
// Bumped v4 -> v5 for the Monte-Carlo / Renditeband feature, same merge-forward pattern
// as the v3 -> v4 bump (not a discard): three new required ForecastFormState fields
// (mcVolatilityPercent, mcPaths, mcSeed) backfill from today's defaults via the existing
// field-by-field mergeScenario below when reading a pre-MC v4 blob.
// Bumped v5 -> v6 for Einmalige Sonderzahlungen: one new required ForecastFormState
// field (extraContributions), same merge-forward pattern as v3->v4/v4->v5 (not a
// discard) — a pre-Sonderzahlungen v5 blob backfills extraContributions: [] via the
// same field-by-field mergeScenario below (mergeScenario is exported so
// lib/scenarioIO.ts's JSON import validator can reuse the identical defaulting logic
// rather than re-implementing it).
// Bumped v6 -> v7 for the N-SCENARIO MODEL: the fixed two-slot shape
// (`{scenarios: {A, B}, activeSlot: 'A'|'B'}`) is replaced by an open-ended named list
// (`{scenarios: {id, scenario}[], activeId}`) so Cockpit can hold any number of
// scenarios, not just two. This is a genuine STRUCTURAL migration (not a field
// backfill): parseLegacyAB below reads the old v6 shape and turns A/B into the first two
// list entries (ids 'a'/'b', in that order) with the SAME field-by-field mergeScenario
// used by every migration above, so nothing in an existing A/B setup is lost — only its
// container shape changes. Same single-level-fallback convention as every bump before it
// (current key, then exactly the ONE immediately-previous key; a scenario still sitting
// in v5 or earlier would not be reached — accepted for the same reason the v4 -> v5 bump
// documented: real usage has had several releases to migrate forward by now).
import { createScenario } from './scenario.js';
import type { BacktestFormState, ForecastFormState, ScenarioState } from './scenario.js';
import { defaultSettings } from './settings.js';
import type { AppSettings } from './settings.js';

const STORAGE_KEY = 'etf-cockpit:scenarios:v7';
const LEGACY_AB_STORAGE_KEY = 'etf-cockpit:scenarios:v6';

/** One entry in the scenario list. `scenario.label` IS the display name (same field
 * that carried "Szenario A"/"Szenario B" before) — `id` is purely a stable identity for
 * list rendering / selection / results lookup, never shown to the user and never sent
 * across the API boundary (list-only, same role as ExtraContributionRow.id). */
export interface ScenarioListEntry {
  id: string;
  scenario: ScenarioState;
}

export interface PersistedState {
  scenarios: ScenarioListEntry[];
  activeId: string;
}

function newScenarioId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `scenario-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Fixed, human-legible ids for the two factory-default scenarios — deliberately NOT
 * random, so a brand-new install's list is deterministic (easy to test/reason about) and
 * so migrateLegacyAB below can target the exact same ids a fresh v7 install would use.
 * Anything added later via addScenario gets a real generated id. */
const DEFAULT_ID_A = 'a';
const DEFAULT_ID_B = 'b';

/** ONE consistent default-naming scheme, used everywhere a scenario needs a fresh
 * factory-default label (fresh install, "+ Neu", the delete-the-last-one fallback, AND
 * "Auf Standard zurücksetzen" — see resetScenario below): "Szenario A", "Szenario B", …
 * by POSITION in the list (0-based), spreadsheet-column style past Z ("Szenario AA",
 * "Szenario AB", …) so it never runs out. Previously the app mixed two different schemes
 * — letters for the factory-default first two ("Szenario A"/"B") vs. plain numbers for
 * everything added afterwards ("Szenario 3", "Szenario 4", …) — which is exactly the
 * "A/B then 3/4" inconsistency reported as a bug. One scheme, one function, every call
 * site below. */
export function scenarioLetter(index: number): string {
  let n = index;
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/** The default label for the scenario that sits at `index` (0-based) in the list —
 * "Szenario A" at 0, "Szenario B" at 1, etc. (see scenarioLetter above). */
export function defaultScenarioLabel(index: number): string {
  return `Szenario ${scenarioLetter(index)}`;
}

export function defaultPersistedState(settings: AppSettings = defaultSettings()): PersistedState {
  const a: ScenarioListEntry = { id: DEFAULT_ID_A, scenario: createScenario(defaultScenarioLabel(0), settings) };
  const b: ScenarioListEntry = { id: DEFAULT_ID_B, scenario: createScenario(defaultScenarioLabel(1), settings) };
  return { scenarios: [a, b], activeId: a.id };
}

function mergeForecastForm(
  raw: Partial<ForecastFormState> | undefined,
  fallback: ForecastFormState,
): ForecastFormState {
  return { ...fallback, ...raw };
}

function mergeBacktestForm(
  raw: Partial<BacktestFormState> | undefined,
  fallback: BacktestFormState,
): BacktestFormState {
  return { ...fallback, ...raw };
}

/** Field-by-field merge over the current default scenario shape, so a scenario stored
 * before a required field existed (e.g. a pre-Entnahmeplan v3 blob) still loads — any
 * field the raw data doesn't have falls back to today's default instead of the whole
 * scenario being discarded. Exported for lib/scenarioIO.ts (JSON import / share-link
 * decode) so both the localStorage migration path and the file-import path share the
 * exact same defaulting logic instead of two copies that could drift apart. */
export function mergeScenario(raw: Partial<ScenarioState> | undefined, fallback: ScenarioState): ScenarioState {
  if (!raw) return fallback;
  return {
    mode: raw.mode === 'backtest' ? 'backtest' : 'forecast',
    label: typeof raw.label === 'string' ? raw.label : fallback.label,
    forecast: mergeForecastForm(raw.forecast, fallback.forecast),
    backtest: mergeBacktestForm(raw.backtest, fallback.backtest),
  };
}

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

/** Parses the CURRENT (v7, list-shaped) storage format defensively: every entry is
 * merged field-by-field over a fresh default scenario (so a partially-written or
 * hand-edited blob still loads instead of crashing/discarding), a missing/non-string `id`
 * gets a fresh one, and an empty/garbage `scenarios` array falls back to the factory
 * default state entirely (never an empty list — "at least 1 scenario always exists").
 * `activeId` falls back to the first entry when it doesn't reference a real entry. */
function parseCurrent(parsed: unknown, settings: AppSettings): PersistedState {
  const fallback = defaultPersistedState(settings);
  if (!isPlainObject(parsed)) return fallback;
  const rawList = Array.isArray(parsed['scenarios']) ? parsed['scenarios'] : [];
  const entries: ScenarioListEntry[] = rawList.map((raw, i) => {
    const r = isPlainObject(raw) ? raw : {};
    const id = typeof r['id'] === 'string' && r['id'] !== '' ? r['id'] : newScenarioId();
    const scenarioFallback = createScenario(defaultScenarioLabel(i), settings);
    const scenario = mergeScenario(r['scenario'] as Partial<ScenarioState> | undefined, scenarioFallback);
    return { id, scenario };
  });
  if (entries.length === 0) return fallback;
  const rawActiveId = parsed['activeId'];
  const activeId =
    typeof rawActiveId === 'string' && entries.some((e) => e.id === rawActiveId) ? rawActiveId : entries[0]!.id;
  return { scenarios: entries, activeId };
}

/** Migrates the LEGACY v6 A/B-slot shape (`{scenarios: {A, B}, activeSlot}`) into the
 * current list shape: A becomes the first entry (id 'a'), B the second (id 'b'), in that
 * order — "A -> first, B -> second", so nothing about which scenario was which is lost.
 * Each of A/B still goes through the same field-by-field mergeScenario every migration
 * before this one used, so an even-older partial A/B blob keeps loading exactly as it did
 * pre-v7. `activeSlot: 'B'` maps to the new entry's id ('b'); anything else (including a
 * missing/garbled field) maps to 'a', matching the old code's own `=== 'B' ? 'B' : 'A'`
 * fallback. */
function parseLegacyAB(parsed: unknown, settings: AppSettings): PersistedState {
  const fallback = defaultPersistedState(settings);
  if (!isPlainObject(parsed)) return fallback;
  const rawScenarios = parsed['scenarios'];
  if (!isPlainObject(rawScenarios)) return fallback;

  const a: ScenarioListEntry = {
    id: DEFAULT_ID_A,
    scenario: mergeScenario(rawScenarios['A'] as Partial<ScenarioState> | undefined, fallback.scenarios[0]!.scenario),
  };
  const b: ScenarioListEntry = {
    id: DEFAULT_ID_B,
    scenario: mergeScenario(rawScenarios['B'] as Partial<ScenarioState> | undefined, fallback.scenarios[1]!.scenario),
  };
  const activeId = parsed['activeSlot'] === 'B' ? DEFAULT_ID_B : DEFAULT_ID_A;
  return { scenarios: [a, b], activeId };
}

export function loadPersistedState(settings: AppSettings = defaultSettings()): PersistedState {
  if (typeof localStorage === 'undefined') return defaultPersistedState(settings);
  try {
    const currentRaw = localStorage.getItem(STORAGE_KEY);
    if (currentRaw) return parseCurrent(JSON.parse(currentRaw), settings);

    const legacyRaw = localStorage.getItem(LEGACY_AB_STORAGE_KEY);
    if (legacyRaw) return parseLegacyAB(JSON.parse(legacyRaw), settings);

    return defaultPersistedState(settings);
  } catch {
    return defaultPersistedState(settings);
  }
}

export function savePersistedState(state: PersistedState): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage full / disabled (private browsing) — silently drop; nothing tax-critical
    // lives only in localStorage, results are always recomputed from the API.
  }
}

// ---- Pure scenario-list transforms (CockpitView's switcher) -----------------------
// Mirrors lib/library.ts's own split: pure list-transform functions here, separate from
// the load/save I/O wrappers above, so add/duplicate/remove/rename/reset are trivially
// unit-testable without touching localStorage. app.tsx owns calling these + persisting
// the result (same "app.tsx owns state, lib does the pure transform" shape as everywhere
// else in this codebase).

function nextDefaultLabel(scenarios: ScenarioListEntry[]): string {
  return defaultScenarioLabel(scenarios.length);
}

/** Appends a brand-new factory-default scenario and makes it active. */
export function addScenario(state: PersistedState, settings: AppSettings = defaultSettings()): PersistedState {
  const entry: ScenarioListEntry = {
    id: newScenarioId(),
    scenario: createScenario(nextDefaultLabel(state.scenarios), settings),
  };
  return { scenarios: [...state.scenarios, entry], activeId: entry.id };
}

/** Duplicates the scenario at `id` (full form state, "<Name> (Kopie)"), inserted right
 * after the source, and makes the copy active. No-op (returns `state` unchanged) if `id`
 * doesn't exist. */
export function duplicateScenario(state: PersistedState, id: string): PersistedState {
  const idx = state.scenarios.findIndex((e) => e.id === id);
  if (idx === -1) return state;
  const source = state.scenarios[idx]!;
  const copy: ScenarioListEntry = {
    id: newScenarioId(),
    scenario: { ...source.scenario, label: `${source.scenario.label} (Kopie)` },
  };
  const scenarios = [...state.scenarios];
  scenarios.splice(idx + 1, 0, copy);
  return { scenarios, activeId: copy.id };
}

/** Removes the scenario at `id`. Deleting the LAST remaining scenario instead recreates
 * one fresh default ("at least 1 scenario always exists" — never an empty list) rather
 * than being refused outright, so the delete action always does something predictable.
 * If the removed scenario was active, activity falls to the first remaining entry. */
export function removeScenario(
  state: PersistedState,
  id: string,
  settings: AppSettings = defaultSettings(),
): PersistedState {
  const remaining = state.scenarios.filter((e) => e.id !== id);
  if (remaining.length === 0) {
    const fresh: ScenarioListEntry = { id: newScenarioId(), scenario: createScenario(defaultScenarioLabel(0), settings) };
    return { scenarios: [fresh], activeId: fresh.id };
  }
  const activeId = state.activeId === id ? remaining[0]!.id : state.activeId;
  return { scenarios: remaining, activeId };
}

/** Renames the scenario at `id` (trims whitespace; a blank name is a no-op rather than
 * saving an empty label). */
export function renameScenario(state: PersistedState, id: string, label: string): PersistedState {
  const trimmed = label.trim();
  if (trimmed === '') return state;
  return {
    ...state,
    scenarios: state.scenarios.map((e) => (e.id === id ? { ...e, scenario: { ...e.scenario, label: trimmed } } : e)),
  };
}

/** Resets the scenario at `id` back to factory defaults — BOTH its parameters AND its
 * name, which is restored to the default label for its current position in the list
 * (defaultScenarioLabel above), never left as whatever custom/stale label it had before
 * the reset. This is a deliberate behaviour change from an earlier version that kept the
 * existing name: a reported bug had a scenario named "Szenario 2" (from the old,
 * inconsistent numeric-suffix scheme) stay "Szenario 2" forever after resetting, even
 * once it was the only — and therefore first — scenario left in the list. "Auf Standard
 * zurücksetzen" now means exactly that: back to a genuinely clean default, name included.
 * No-op (returns `state` unchanged) if `id` doesn't exist. */
export function resetScenario(
  state: PersistedState,
  id: string,
  settings: AppSettings = defaultSettings(),
): PersistedState {
  const idx = state.scenarios.findIndex((e) => e.id === id);
  if (idx === -1) return state;
  return {
    ...state,
    scenarios: state.scenarios.map((e, i) =>
      i === idx ? { ...e, scenario: createScenario(defaultScenarioLabel(i), settings) } : e,
    ),
  };
}

/** Replaces the scenario at `id`'s form state wholesale (the editor's onChange path). */
export function updateScenarioInList(state: PersistedState, id: string, scenario: ScenarioState): PersistedState {
  return { ...state, scenarios: state.scenarios.map((e) => (e.id === id ? { ...e, scenario } : e)) };
}

/** Sets the active (Cockpit-edited) scenario. No-op if `id` doesn't exist. */
export function setActiveScenario(state: PersistedState, id: string): PersistedState {
  return state.scenarios.some((e) => e.id === id) ? { ...state, activeId: id } : state;
}

/** Appends `scenario` as a brand-new list entry and makes it active — used by the
 * "load into a new scenario" actions (Szenario-Bibliothek, JSON import, shared link),
 * as an alternative to overwriting an existing entry. */
export function addScenarioFrom(state: PersistedState, scenario: ScenarioState): PersistedState {
  const entry: ScenarioListEntry = { id: newScenarioId(), scenario };
  return { scenarios: [...state.scenarios, entry], activeId: entry.id };
}
